import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileSignature, Save, Download, CheckCircle2, RotateCcw, Trash2, Mail, Printer, ShieldCheck, FilePlus2, Edit3 } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/download";
import { AdultContractSheet } from "@/components/AdultContractSheet";

const toNumber = (v) => {
    const s = String(v ?? "").trim();
    return Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s) || 0;
};

/**
 * Panel del contrato "Neurorrehabilitación Adultos 2026". Se muestra en
 * Cumplimiento Legal (o en el modo Quiosco con la variante `kiosk`).
 * Estados: draft (editable) o signed (solo lectura + descarga).
 */
export default function AdultContractPanel({ patientId, kiosk = false, apiClient = null,
    authHeaders = null, onChange, onSigned, currentUserRole = null, annexId = null,
    onAnnexCreated = null, hideAnnexes = false }) {
    const clientRef = useRef(apiClient || api);
    const authRef = useRef(authHeaders);
    useEffect(() => { authRef.current = authHeaders; }, [authHeaders]);
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [signOpen, setSignOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sigEmpty, setSigEmpty] = useState(true);
    const [signerName, setSignerName] = useState("");
    const sigRef = useRef(null);
    // Director signature state
    const [directorSignOpen, setDirectorSignOpen] = useState(false);
    const [dirSigEmpty, setDirSigEmpty] = useState(true);
    const [directorName, setDirectorName] = useState("");
    const [signingDirector, setSigningDirector] = useState(false);
    const dirSigRef = useRef(null);
    // Email state
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailValue, setEmailValue] = useState("");
    const [sendingEmail, setSendingEmail] = useState(false);
    // Logo blob URL
    const [logoUrl, setLogoUrl] = useState(null);
    // Annexes state (main contract only)
    const [annexes, setAnnexes] = useState([]);
    const [creatingAnnex, setCreatingAnnex] = useState(false);
    const [openAnnexId, setOpenAnnexId] = useState(null);
    const [layout, setLayout] = useState(null);

    useEffect(() => {
        clientRef.current.get("/public/adult-contract/layout")
            .then((r) => setLayout(r.data))
            .catch(() => toast.error("No se pudo cargar la plantilla del contrato"));
    }, []);

    const isAnnex = !!annexId;
    const baseUrl = kiosk
        ? "/kiosk/adult-contract"
        : (isAnnex
            ? `/patients/${patientId}/adult-contract/annexes/${annexId}`
            : `/patients/${patientId}/adult-contract`);
    const config = () => (authRef.current ? { headers: authRef.current } : {});
    const isDirectivo = (currentUserRole === "directivo" || currentUserRole === "secretaria");
    const isPatient = (currentUserRole === "paciente");

    // Cargar logo (una sola vez). En modo kiosco usa endpoint público (sin auth).
    useEffect(() => {
        let cancelled = false; let objectUrl = null;
        (async () => {
            try {
                const url = kiosk ? "/public/branding/logo" : "/settings/branding/logo";
                const cfg = kiosk ? { responseType: "blob" } : { responseType: "blob", ...config() };
                const r = await clientRef.current.get(url, cfg);
                if (cancelled) return;
                objectUrl = URL.createObjectURL(new Blob([r.data], { type: r.headers?.["content-type"] || "image/png" }));
                setLogoUrl(objectUrl);
            } catch (_e) { /* sin logo: OK */ }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [kiosk]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await clientRef.current.get(baseUrl, config());
            setContract(data);
            setSignerName(data?.signed_by_name || data?.rep_name || data?.user_name || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo cargar el contrato");
        } finally { setLoading(false); }
    }, [baseUrl]);
    useEffect(() => { load(); }, [load]);

    // Cargar lista de anexos (solo contrato principal, no en anexo ni en kiosk)
    const loadAnnexes = useCallback(async () => {
        if (isAnnex || kiosk || hideAnnexes || !patientId) return;
        try {
            const { data } = await clientRef.current.get(
                `/patients/${patientId}/adult-contract/annexes`, config());
            setAnnexes(data || []);
        } catch (_e) { /* silencioso */ }
    }, [patientId, isAnnex, kiosk, hideAnnexes]);
    useEffect(() => { loadAnnexes(); }, [loadAnnexes]);

    const isSigned = contract?.status === "signed";

    const set = (patch) => setContract((c) => ({ ...(c || {}), ...patch }));

    const save = async () => {
        if (isSigned) return false;
        setSaving(true);
        try {
            const payload = { ...contract };
            delete payload.status; delete payload.signature_data_url;
            delete payload.signed_at; delete payload.signed_by_name;
            delete payload.signed_pdf_s3_key;
            delete payload.signed_pdf_storage; delete payload._id; delete payload.id;
            delete payload.created_at; delete payload.updated_at;
            delete payload.patient_id;
            payload.price_per_session = toNumber(payload.price_per_session);
            payload.monthly_estimated = toNumber(payload.monthly_estimated);
            const { data } = await clientRef.current.put(baseUrl, payload, config());
            setContract(data);
            toast.success("Borrador guardado");
            onChange && onChange();
            return true;
        } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); return false; }
        finally { setSaving(false); }
    };

    const download = async () => {
        try {
            const cfg = { responseType: "blob", ...config() };
            const url = kiosk
                ? "/kiosk/adult-contract/pdf"
                : (isAnnex
                    ? `/patients/${patientId}/adult-contract/annexes/${annexId}/pdf?signed=${isSigned}`
                    : `/patients/${patientId}/adult-contract/pdf?signed=${isSigned}`);
            const r = await clientRef.current.get(url, cfg);
            const prefix = isAnnex ? "Anexo-contrato" : "Contrato-Adultos";
            downloadBlob(r.data, `${prefix}-${isSigned ? "firmado" : "borrador"}.pdf`);
        } catch (e) { toast.error("No se pudo descargar el PDF"); }
    };

    const printPdf = async () => {
        try {
            const cfg = { responseType: "blob", ...config() };
            const url = kiosk
                ? "/kiosk/adult-contract/pdf"
                : (isAnnex
                    ? `/patients/${patientId}/adult-contract/annexes/${annexId}/pdf?signed=true`
                    : `/patients/${patientId}/adult-contract/pdf?signed=true`);
            const r = await clientRef.current.get(url, cfg);
            const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const w = window.open(blobUrl, "_blank");
            if (!w) { toast.error("Permite ventanas emergentes para imprimir"); return; }
            const trigger = () => { try { w.focus(); w.print(); } catch (_e) { } };
            w.addEventListener("load", trigger);
            setTimeout(trigger, 900);
        } catch (e) { toast.error("No se pudo abrir el PDF para imprimir"); }
    };

    const openEmail = () => {
        setEmailValue(contract?.email_sent_to || contract?.user_email || "");
        setEmailOpen(true);
    };
    const sendEmail = async () => {
        if (!emailValue.trim() || !emailValue.includes("@")) {
            toast.error("Introduce un email válido"); return;
        }
        setSendingEmail(true);
        try {
            const url = kiosk
                ? "/kiosk/adult-contract/send-email"
                : (isAnnex
                    ? `/patients/${patientId}/adult-contract/annexes/${annexId}/send-email`
                    : `/patients/${patientId}/adult-contract/send-email`);
            await clientRef.current.post(url, { email: emailValue }, config());
            toast.success(`Enviado a ${emailValue}`);
            setEmailOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo enviar el email");
        } finally { setSendingEmail(false); }
    };

    const openDirectorSign = () => {
        setDirectorName(contract?.director_signed_name || "");
        setDirectorSignOpen(true);
        setDirSigEmpty(true);
        setTimeout(() => dirSigRef.current?.clear(), 40);
    };
    const submitDirectorSign = async () => {
        if (!dirSigRef.current || dirSigRef.current.isEmpty()) {
            toast.error("Por favor firma antes de continuar"); return;
        }
        setSigningDirector(true);
        try {
            const dataUrl = dirSigRef.current.getCanvas().toDataURL("image/png");
            const url = isAnnex
                ? `/patients/${patientId}/adult-contract/annexes/${annexId}/director-sign`
                : `/patients/${patientId}/adult-contract/director-sign`;
            const { data } = await clientRef.current.post(url,
                { signature_data_url: dataUrl, director_name: directorName },
                config(),
            );
            setContract(data);
            toast.success("Firma de dirección añadida");
            setDirectorSignOpen(false);
            onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Error al firmar como dirección"); }
        finally { setSigningDirector(false); }
    };

    const reopen = async () => {
        if (!window.confirm("¿Reabrir el contrato firmado para volver a editarlo?")) return;
        try {
            await clientRef.current.post(`/patients/${patientId}/adult-contract/reopen`, {}, config());
            toast.success("Contrato reabierto para edición");
            load(); onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "No se pudo reabrir"); }
    };

    const openSign = () => {
        setSignerName(contract?.rep_name || contract?.user_name || "");
        setSignOpen(true);
        setSigEmpty(true);
        setTimeout(() => sigRef.current?.clear(), 40);
    };
    const submitSign = async () => {
        if (!sigRef.current || sigRef.current.isEmpty()) {
            toast.error("Por favor firma antes de continuar"); return;
        }
        if (!signerName.trim()) {
            toast.error("Introduce el nombre de quien firma"); return;
        }
        setSubmitting(true);
        try {
            // Guarda primero para asegurar que los últimos cambios están en el server
            if (!(await save())) return;
            const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
            const url = kiosk
                ? "/kiosk/adult-contract/sign"
                : (isAnnex
                    ? `/patients/${patientId}/adult-contract/annexes/${annexId}/sign`
                    : (isPatient
                        ? `/patients/${patientId}/adult-contract/sign-self`
                        : `/patients/${patientId}/adult-contract/sign`));
            const { data } = await clientRef.current.post(url, {
                signature_data_url: dataUrl,
                signed_by_name: signerName,
                signed_city: contract?.signed_city || "Madrid",
            }, config());
            setContract(data);
            toast.success("Contrato firmado correctamente");
            setSignOpen(false);
            onSigned && onSigned(data);
            onChange && onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Error al firmar"); }
        finally { setSubmitting(false); }
    };

    const createAnnex = async () => {
        if (!isDirectivo || isAnnex || kiosk) return;
        setCreatingAnnex(true);
        try {
            const { data } = await clientRef.current.post(
                `/patients/${patientId}/adult-contract/annexes`, {}, config());
            toast.success("Anexo creado. Edítalo y fírmalo cuando esté listo.");
            await loadAnnexes();
            setOpenAnnexId(data.id);
            onAnnexCreated && onAnnexCreated(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo crear el anexo");
        } finally { setCreatingAnnex(false); }
    };
    const deleteAnnex = async (aid) => {
        if (!window.confirm("¿Eliminar este anexo (solo borrador)?")) return;
        try {
            await clientRef.current.delete(
                `/patients/${patientId}/adult-contract/annexes/${aid}`, config());
            toast.success("Anexo eliminado");
            loadAnnexes();
        } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
    };
    const downloadAnnex = async (aid, signed) => {
        try {
            const cfg = { responseType: "blob", ...config() };
            const r = await clientRef.current.get(
                `/patients/${patientId}/adult-contract/annexes/${aid}/pdf?signed=${signed}`, cfg);
            downloadBlob(r.data, `Anexo-contrato-${signed ? "firmado" : "borrador"}.pdf`);
        } catch (e) { toast.error("No se pudo descargar el anexo"); }
    };

    if (loading || !contract) return <div className="text-sm text-muted-foreground">Cargando contrato…</div>;
    const disabled = isSigned;
    const legacySigned = isSigned && contract.template_version !== "2026-pdf";
    const annexTitle = isAnnex
        ? `ANEXO CONTRATO · ${contract.base_signed_date || (contract.annex_of_signed_at || "").slice(0, 10) || "s/f"}`
        : undefined;

    return (
        <Card className="p-5 space-y-5" data-testid="adult-contract-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                    {logoUrl && (
                        <img src={logoUrl} alt="Logo Boreal"
                            className="w-14 h-14 object-contain rounded-md border border-border bg-white p-1"
                            data-testid="adult-contract-logo" />
                    )}
                    <div>
                        <div className="uppercase-label text-primary flex items-center gap-1">
                            <FileSignature size={12} /> {isAnnex ? "Anexo del contrato" : "Contrato Neurorrehabilitación Adultos 2026"}
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight mt-1">
                            {isAnnex ? `Anexo contrato · ${contract?.base_signed_date || (contract?.annex_of_signed_at || "").slice(0,10)}` : "Contrato de servicios"}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            {isAnnex ? "Modifica lo necesario y vuelve a firmar. Se generará un PDF de anexo con los mismos datos y las nuevas firmas." : "Rellena los datos y firma digitalmente. Se puede firmar aquí o desde el modo Quiosco."}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isSigned ? (
                        <Badge variant="outline" className="text-primary border-primary/40">
                            <CheckCircle2 size={12} className="mr-1" /> Firmado{" "}
                            {contract.signed_at ? `· ${contract.signed_at.slice(0, 10)}` : ""}
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">Borrador · Pendiente de firma</Badge>
                    )}
                    <Button variant="outline" size="sm" onClick={download} data-testid="adult-contract-download"><Download size={13} className="mr-1" /> {isSigned ? "Descargar" : "Vista previa PDF"}</Button>
                    {isSigned && (
                        <>
                            <Button variant="outline" size="sm" onClick={printPdf} data-testid="adult-contract-print"><Printer size={13} className="mr-1" /> Imprimir</Button>
                            <Button variant="outline" size="sm" onClick={openEmail} data-testid="adult-contract-email"><Mail size={13} className="mr-1" /> Enviar por email</Button>
                        </>
                    )}
                    {isSigned && !kiosk && !isAnnex && <Button variant="ghost" size="sm" onClick={reopen} data-testid="adult-contract-reopen"><RotateCcw size={13} className="mr-1" /> Reabrir</Button>}
                </div>
            </div>

            {legacySigned ? (
                <p className="text-sm rounded-md border border-border bg-secondary/40 p-3" data-testid="adult-contract-legacy-note">
                    Este contrato se firmó con la versión anterior del documento. Usa «Descargar» para ver exactamente lo que se firmó.
                </p>
            ) : (
                <div className="overflow-x-auto rounded-md bg-secondary/40 p-2 sm:p-4" data-testid="adult-contract-sheet-wrap">
                    {!disabled && <p className="text-xs text-muted-foreground mb-2">Escribe directamente sobre el documento en las zonas sombreadas y marca las casillas que correspondan.</p>}
                    <AdultContractSheet doc={{ ...contract, annex_title: annexTitle }} set={set} readOnly={disabled} layout={layout} />
                </div>
            )}
            {contract.payment_method === "domiciliacion" && (
                <p className="text-xs text-primary" data-testid="ac-sepa-hint">
                    Con domiciliación se pedirá la orden SEPA a continuación del documento de protección de datos.
                </p>
            )}

            {isSigned && (
                <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm space-y-3" data-testid="adult-contract-signed-block">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div data-testid="adult-contract-signed-patient">
                            <div className="uppercase-label text-xs text-primary mb-1">Usuario / Representante</div>
                            <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 size={14} className="text-primary" />
                                <span className="font-medium text-primary">{contract.signed_by_name}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                En {contract.signed_city || "Madrid"} · {contract.signed_date || ""}
                                {contract.signed_by_kiosk ? " · Quiosco" : ""}
                            </div>
                            {contract.signature_data_url && (
                                <img src={contract.signature_data_url} alt="Firma"
                                    className="mt-2 max-h-24 bg-white rounded border border-border" />
                            )}
                        </div>
                        <div data-testid="adult-contract-signed-director">
                            <div className="uppercase-label text-xs text-primary mb-1">Dirección · EN Boreal</div>
                            {contract.director_signature_data_url ? (
                                <>
                                    <div className="flex items-center gap-2 mb-1">
                                        <CheckCircle2 size={14} className="text-primary" />
                                        <span className="font-medium text-primary">{contract.director_signed_name || "Dirección"}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Firmado el {contract.director_signed_date || (contract.director_signed_at ? contract.director_signed_at.slice(0, 10) : "")}
                                    </div>
                                    <img src={contract.director_signature_data_url} alt="Firma dirección"
                                        className="mt-2 max-h-24 bg-white rounded border border-border" />
                                </>
                            ) : (
                                <div className="text-xs text-muted-foreground italic mb-2">Pendiente de firma por dirección</div>
                            )}
                            {!contract.director_signature_data_url && isDirectivo && !kiosk && (
                                <Button size="sm" className="btn-cta mt-1" onClick={openDirectorSign} data-testid="adult-contract-director-sign">
                                    <ShieldCheck size={13} className="mr-1" /> Firmar como dirección
                                </Button>
                            )}
                        </div>
                    </div>
                    {contract.email_sent_at && (
                        <div className="text-xs text-muted-foreground" data-testid="adult-contract-email-sent-info">
                            Último envío por email: {contract.email_sent_at.slice(0, 10)} · {contract.email_sent_to}
                        </div>
                    )}
                </div>
            )}

            {/* Anexos: solo contrato principal firmado y no kiosk */}
            {isSigned && !kiosk && !isAnnex && !hideAnnexes && (
                <section className="space-y-2 border-t border-border pt-4" data-testid="adult-contract-annexes-section">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="uppercase-label text-primary">Anexos al contrato</div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Un anexo clona los datos actuales del contrato firmado para poder modificarlos y volver a firmar.
                            </p>
                        </div>
                        {isDirectivo && (
                            <Button size="sm" className="btn-cta" onClick={createAnnex} disabled={creatingAnnex} data-testid="adult-contract-annex-create">
                                <FilePlus2 size={13} className="mr-1" /> {creatingAnnex ? "Creando…" : "Crear anexo"}
                            </Button>
                        )}
                    </div>
                    {annexes.length === 0 && (
                        <div className="text-xs text-muted-foreground italic pt-1" data-testid="adult-contract-annex-empty">
                            Sin anexos todavía.
                        </div>
                    )}
                    <div className="space-y-2">
                        {annexes.map((a) => {
                            const title = `Anexo contrato · ${a.base_signed_date || (a.annex_of_signed_at || "").slice(0, 10)}`;
                            const isASigned = a.status === "signed";
                            return (
                                <div key={a.id} className="rounded-md border border-border p-3 flex items-center justify-between gap-2 flex-wrap"
                                    data-testid={`adult-contract-annex-item-${a.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-sm">{title}</span>
                                            {isASigned ? (
                                                <Badge variant="outline" className="text-primary border-primary/40 text-[10px]">
                                                    <CheckCircle2 size={10} className="mr-0.5" /> Firmado
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">Borrador</Badge>
                                            )}
                                            {a.director_signature_data_url && (
                                                <Badge variant="outline" className="text-primary border-primary/40 text-[10px]">
                                                    <ShieldCheck size={10} className="mr-0.5" /> Dirección
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            Creado {a.created_at?.slice(0, 10)} por {a.created_by_name || "—"}
                                            {isASigned && a.signed_date ? ` · Firmado ${a.signed_date}` : ""}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {isDirectivo && (
                                            <Button size="sm" variant="outline" onClick={() => setOpenAnnexId(a.id)}
                                                data-testid={`adult-contract-annex-open-${a.id}`}>
                                                <Edit3 size={12} className="mr-1" /> Abrir
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => downloadAnnex(a.id, isASigned)}
                                            data-testid={`adult-contract-annex-download-${a.id}`}>
                                            <Download size={12} className="mr-1" /> PDF
                                        </Button>
                                        {isDirectivo && !isASigned && (
                                            <Button size="sm" variant="ghost" onClick={() => deleteAnnex(a.id)}
                                                data-testid={`adult-contract-annex-delete-${a.id}`}>
                                                <Trash2 size={12} className="text-red-700" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {!disabled && (
                <div className="flex justify-end gap-2 flex-wrap border-t border-border pt-3">
                    <Button variant="outline" onClick={save} disabled={saving} data-testid="adult-contract-save">
                        <Save size={13} className="mr-1" /> {saving ? "Guardando…" : "Guardar borrador"}
                    </Button>
                    <Button className="btn-cta" onClick={openSign} data-testid="adult-contract-sign">
                        <FileSignature size={13} className="mr-1" /> Firmar contrato
                    </Button>
                </div>
            )}

            <Dialog open={signOpen} onOpenChange={(v) => !v && setSignOpen(false)}>
                <DialogContent className="max-w-lg" data-testid="adult-contract-sign-dialog">
                    <DialogHeader>
                        <DialogTitle>Firma del contrato</DialogTitle>
                        <DialogDescription>
                            Al firmar declaras haber leído y aceptado el contrato. Se guardará el PDF firmado.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs mb-1 block">Nombre y apellidos de quien firma</Label>
                            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                                data-testid="adult-contract-signer-name" />
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Firma manuscrita</Label>
                            <div className="border rounded-md bg-white overflow-hidden">
                                <SignatureCanvas ref={sigRef}
                                    canvasProps={{
                                        width: 460, height: 180,
                                        className: "w-full h-44",
                                        "data-testid": "adult-contract-sig-canvas",
                                    }}
                                    onBegin={() => setSigEmpty(false)} />
                            </div>
                            <div className="flex justify-between mt-1">
                                <span className="text-[11px] text-muted-foreground">
                                    {sigEmpty ? "Firma dentro del recuadro" : "Firma capturada"}
                                </span>
                                <Button variant="ghost" size="sm" onClick={() => { sigRef.current?.clear(); setSigEmpty(true); }} data-testid="adult-contract-sig-clear">
                                    <Trash2 size={12} className="mr-1" /> Limpiar
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSignOpen(false)}>Cancelar</Button>
                        <Button className="btn-cta" onClick={submitSign} disabled={submitting} data-testid="adult-contract-sign-submit">
                            {submitting ? "Firmando…" : "Firmar y guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={directorSignOpen} onOpenChange={(v) => !v && setDirectorSignOpen(false)}>
                <DialogContent className="max-w-lg" data-testid="adult-contract-director-sign-dialog">
                    <DialogHeader>
                        <DialogTitle>Firma de dirección</DialogTitle>
                        <DialogDescription>
                            Firma como representante de EN Boreal. Se añadirá al PDF junto a la firma del paciente.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs mb-1 block">Nombre del director / directora</Label>
                            <Input value={directorName} onChange={(e) => setDirectorName(e.target.value)}
                                placeholder="Nombre y apellidos"
                                data-testid="adult-contract-director-name" />
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Firma manuscrita</Label>
                            <div className="border rounded-md bg-white overflow-hidden">
                                <SignatureCanvas ref={dirSigRef}
                                    canvasProps={{
                                        width: 460, height: 180,
                                        className: "w-full h-44",
                                        "data-testid": "adult-contract-director-sig-canvas",
                                    }}
                                    onBegin={() => setDirSigEmpty(false)} />
                            </div>
                            <div className="flex justify-between mt-1">
                                <span className="text-[11px] text-muted-foreground">
                                    {dirSigEmpty ? "Firma dentro del recuadro" : "Firma capturada"}
                                </span>
                                <Button variant="ghost" size="sm" onClick={() => { dirSigRef.current?.clear(); setDirSigEmpty(true); }} data-testid="adult-contract-director-sig-clear">
                                    <Trash2 size={12} className="mr-1" /> Limpiar
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDirectorSignOpen(false)}>Cancelar</Button>
                        <Button className="btn-cta" onClick={submitDirectorSign} disabled={signingDirector} data-testid="adult-contract-director-sign-submit">
                            {signingDirector ? "Firmando…" : "Firmar como dirección"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={emailOpen} onOpenChange={(v) => !v && setEmailOpen(false)}>
                <DialogContent className="max-w-md" data-testid="adult-contract-email-dialog">
                    <DialogHeader>
                        <DialogTitle>Enviar contrato por email</DialogTitle>
                        <DialogDescription>
                            El destinatario recibirá un enlace de descarga del PDF firmado.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-xs">Email destinatario</Label>
                        <Input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)}
                            placeholder="paciente@ejemplo.com" data-testid="adult-contract-email-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancelar</Button>
                        <Button className="btn-cta" onClick={sendEmail} disabled={sendingEmail} data-testid="adult-contract-email-submit">
                            <Mail size={13} className="mr-1" /> {sendingEmail ? "Enviando…" : "Enviar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sub-panel de anexo: reutiliza este mismo componente con annexId */}
            <Dialog open={!!openAnnexId} onOpenChange={(v) => !v && setOpenAnnexId(null)}>
                <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="adult-contract-annex-dialog">
                    <div className="p-5">
                        {openAnnexId && (
                            <AdultContractPanel
                                patientId={patientId}
                                annexId={openAnnexId}
                                currentUserRole={currentUserRole}
                                onChange={() => loadAnnexes()}
                                onSigned={() => loadAnnexes()}
                                hideAnnexes
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
